/**
 * Built-in data source registration.
 * Instantiates and registers all 23 built-in data sources with the
 * source manager and collection scheduler.
 */

import { sourceManager } from '../source-manager';
import { scheduler } from '@/core/scheduler';

// --- Original 8 sources ---
import { usgsSource } from './usgs-source';
import { firmsSource } from './firms-source';
import { gdacsSource } from './gdacs-source';
import { acledSource } from './acled-source';
import { gdeltSource } from './gdelt-source';
import { openskySource } from './opensky-source';
import { vesselSource } from './vessel-source';
import { sanctionsSource } from './sanctions-source';

// --- Phase 3: 12 new sources ---
import { telegramSource } from './telegram-source';
import { orefSource } from './oref-source';
import { gpsjamSource } from './gpsjam-source';
import { cyberSource } from './cyber-source';
import { ucdpSource } from './ucdp-source';
import { outageSource } from './outage-source';
import { advisorySource } from './advisory-source';
import { eonetSource } from './eonet-source';
import { weatherSource } from './weather-source';
import { cableHealthSource } from './cable-health-source';
import { militaryBasesSource } from './military-bases-source';
import { aisStreamSource } from './ais-stream-source';

// --- Phase D: 3 new sources ---
import { fredSource } from './fred-source';
import { displacementSource } from './displacement-source';
import { airportDelaySource } from './airport-delay-source';

// --- Session 4: USNI Fleet Tracker ---
import { usniSource } from './usni-source';

// --- Phase E: Market data source ---
import { marketSource } from './market-source';

import type { Source } from '../base-source';

/** All built-in sources in registration order. */
const BUILT_IN_SOURCES: Source[] = [
  // Original 8
  usgsSource,
  firmsSource,
  gdacsSource,
  acledSource,
  gdeltSource,
  openskySource,
  vesselSource,
  sanctionsSource,
  // Phase 3: 12 new sources
  telegramSource,
  orefSource,
  gpsjamSource,
  cyberSource,
  ucdpSource,
  outageSource,
  advisorySource,
  eonetSource,
  weatherSource,
  cableHealthSource,
  militaryBasesSource,
  aisStreamSource,
  // Phase D: 3 new sources
  fredSource,
  displacementSource,
  airportDelaySource,
  // Session 4: USNI Fleet Tracker
  usniSource,
  // Phase E: Market data
  marketSource,
];

/**
 * Check if a source has its required API key(s) configured.
 * Checks environment variables and localStorage for each config field
 * marked as required with type 'password' or 'text'.
 */
/** Map from source config keys to VITE_ environment variable names. */
const ENV_KEY_MAP: Record<string, string> = {
  'telegram-botToken': 'VITE_TELEGRAM_SESSION',
  'acled-email': 'VITE_ACLED_EMAIL',
  'acled-password': 'VITE_ACLED_PASSWORD',
  'opensky-username': 'VITE_OPENSKY_USERNAME',
  'opensky-password': 'VITE_OPENSKY_PASSWORD',
  'vesselfinder-apiKey': 'VITE_VESSELFINDER_API_KEY',
  'ais-ws-apiKey': 'VITE_AIS_API_KEY',
  'fred-apiKey': 'VITE_FRED_API_KEY',
};

function hasRequiredKeys(source: Source): boolean {
  if (!source.meta.requiresApiKey) return true;

  // Check if any required config fields have values in localStorage or env
  for (const field of source.configSchema) {
    if (!field.required) continue;
    // Check localStorage first (Settings panel saves here)
    try {
      const stored = localStorage.getItem(`sentinel-key-${source.id}-${field.key}`);
      if (stored) return true;
    } catch { /* localStorage unavailable */ }
    // Fall back to environment variable
    const envKey = ENV_KEY_MAP[`${source.id}-${field.key}`];
    if (envKey && import.meta.env[envKey]) return true;
  }

  return false;
}

/**
 * Register all built-in data sources.
 * Initializes each source, registers with source manager, and
 * schedules periodic collection.
 *
 * Sources that require API keys but have none configured are registered
 * with 'disabled' status and not scheduled for collection.
 */
export async function registerBuiltInSources(): Promise<void> {
  console.log(`[Sentinel] Registering ${BUILT_IN_SOURCES.length} built-in data sources`);

  let disabled = 0;

  const results = await Promise.allSettled(
    BUILT_IN_SOURCES.map(async (source) => {
      try {
        // Check for required API keys before scheduling
        if (source.meta.requiresApiKey && !hasRequiredKeys(source)) {
          await source.initialize({});
          sourceManager.register(source);
          // Don't schedule — mark as disabled quietly
          await source.stop(); // Sets state to 'disabled'
          disabled++;
          console.debug(`[Sentinel] Source ${source.id} disabled — API key required (${source.meta.apiKeyUrl ?? 'see settings'})`);
          return;
        }

        await source.initialize({});
        sourceManager.register(source);
        scheduler.register(source, source.meta.updateInterval);
        await source.start();
        console.debug(`[Sentinel] Source registered: ${source.id} (${source.getStatus().state})`);
      } catch (err) {
        console.warn(`[Sentinel] Failed to register source "${source.id}":`, err);
        throw err; // Let allSettled capture the failure
      }
    }),
  );

  const succeeded = results.filter(r => r.status === 'fulfilled').length;
  const failed = results.filter(r => r.status === 'rejected').length;
  console.log(`[Sentinel] Sources: ${succeeded - disabled} active, ${disabled} disabled (no API key), ${failed} failed`);
}
